import { apiUrls } from 'constants/apiUrls';
import { ApiBase } from './API';
import { AxiosRequestConfig } from 'axios';
import {
  FileInfo,
  IdNameModel,
  LayerDataCount,
  LayerDataRequest,
  DataProfile,
  LayerProperties,
  QueryPreviewType,
  DataSourceAlarmMetadata,
} from 'types/types';

export interface INIService {
  GetNotificationData: () => Promise<any>;
  GetApplicationSettings: () => Promise<any>;
  DownloadUserGuide: () => Promise<any>;
  DownloadReleaseNotes: () => Promise<any>;
  GetMenuItems: () => Promise<any>;
  checkExportStatuses: () => Promise<FileInfo[]>;
  GetUserProperties: () => Promise<any>;
  AddUserProerties: (data: any) => Promise<any>;
  GetDataSourceMetadataForAlarm: (dataSourceId: string) => Promise<any>;
  GetDataSourceDataForAlarm: () => Promise<IdNameModel[]>;
  GetGeoJSONLayersMetadata: () => Promise<string[]>;
  GetDataProfilesByDataSourceId: (
    dataSourceId: string
  ) => Promise<{ profiles: DataProfile[]; users: string[] }>;
  GetQueryPreviewByDataProfile: (
    dataProfile: DataProfile
  ) => Promise<{ data: QueryPreviewType }>;
  GetQueryPreviewByQueryRule: (
    queryRule: any,
    sourceElementId: string,
    aggregationLevelId: string
  ) => Promise<{ data: QueryPreviewType }>;
  CreateDataProfiles: (body: any, useExistingGroups: boolean) => Promise<void>;
}

class NIService extends ApiBase implements INIService {
  constructor() {
    super('');
  }

  public GetNotificationData = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.notificationsUrl);
  };

  public GetApplicationSettings = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.applicationSettingsUrl);
  };

  public DownloadUserGuide = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.userGuideUrl);
  };

  public DownloadReleaseNotes = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.releaseNotesUrl);
  };

  public GetMenuItems = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.navigationUrl);
  };

  public checkExportStatuses = async (): Promise<FileInfo[]> => {
    return this.instance.post<any>(apiUrls.exportStatusUrl);
  };

  public GetUserProperties = async (): Promise<any> => {
    return this.instance.get<any>(apiUrls.userPropertiesUrl);
  };

  public AddUserProerties = async (data: any): Promise<any> => {
    return this.instance.post<any>(apiUrls.addPropertyUrl, data);
  };

  public GetDataSourceDataForAlarm = async (): Promise<IdNameModel[]> => {
    return this.instance.get<IdNameModel[]>(apiUrls.alarmNiDataSourceDataUrl);
  };

  public GetDataSourceMetadataForAlarm = async (
    dataSourceId: string
  ): Promise<DataSourceAlarmMetadata> => {
    return this.instance.post<DataSourceAlarmMetadata>(
      `${apiUrls.dataSourceMetadataForAlarmUrl}?elementType=${dataSourceId}`
    );
  };

  public GetGeoJSONLayersMetadata = async (): Promise<string[]> => {
    return this.instance.get<string[]>(apiUrls.geoJSONLayersMetadataUrl);
  };

  public GetGeoJSONLayerData = async (
    layerProperties: LayerProperties,
    config?: AxiosRequestConfig
  ): Promise<any> => {
    return this.instance.post<any>(
      apiUrls.geoJSONLayerDataUrl,
      layerProperties,
      config
    );
  };

  public GetDataProfilesByDataSourceId = async (
    dataSourceId: string
  ): Promise<{ profiles: DataProfile[]; users: string[] }> => {
    return this.instance.get<{ profiles: DataProfile[]; users: string[] }>(
      `${apiUrls.geoDatProfilesByDataElementIdUrl}?dataElementId=${dataSourceId}`
    );
  };

  public GetQueryPreviewByDataProfile = async (
    dataProfile: DataProfile
  ): Promise<{ data: QueryPreviewType }> => {
    return this.instance.post<{ data: QueryPreviewType }>(
      apiUrls.getQueryPreviewByDataProfileUrl,
      dataProfile
    );
  };

  public GetQueryPreviewByQueryRule = async (
    queryRule: any,
    sourceElementId: string,
    aggregationLevelId: string
  ): Promise<{ data: QueryPreviewType }> => {
    return this.instance.post<{ data: QueryPreviewType }>(
      `${apiUrls.getQueryPreviewByQueryRuleUrl}?sourceElementId=${sourceElementId}&aggregationLevelId=${aggregationLevelId}`,
      queryRule
    );
  };

  public CreateDataProfiles = async (
    body: any,
    useExistingGroups: boolean
  ): Promise<void> => {
    return this.instance.post(
      `${apiUrls.createDataProfiles}?useExistingGroups=${useExistingGroups}`,
      body
    );
  };
}

export default NIService;
